import React, { ReactNode, useContext } from 'react'
import { FieldArray, useFormikContext } from 'formik'
import FillGlyph from '../shared/dosing_modes/FillGlyph'
import { FillLevel } from '../shared/dosing_modes/fillLevels'
import { FormReadOnlyContext } from '../shared/forms/FormReadOnlyContext'
import { NumberField, Unit, modeLabel } from './FeederDosingModeFields'
import {
  FeederDosingModeValues,
  emptyEmergencyStep
} from './feederDosingModeValues'

interface ParameterRow {
  label: string
  // Path below the column's record, e.g. "fast_parameter.pressure"
  field: string
  unit: Unit
  decimals?: number
  // Rows sharing a group get a labelled group row above them
  group?: string
}

const PARAMETER_ROWS = (prefix: string, group: string): ParameterRow[] => [
  { label: 'pressure', field: `${prefix}.pressure`, unit: 'bar', group },
  { label: 'frequency', field: `${prefix}.frequency`, unit: 'hz', group },
  { label: 'duty_cycle', field: `${prefix}.duty_cycle`, unit: 'percent', group }
]

const LEVEL_ROWS: ParameterRow[] = [
  ...PARAMETER_ROWS('fast_parameter', 'fast_dosing'),
  ...PARAMETER_ROWS('slow_parameter', 'slow_dosing')
]

const STEP_ROWS: ParameterRow[] = [
  { label: 'pressure', field: 'pressure', unit: 'bar' },
  { label: 'frequency', field: 'frequency', unit: 'hz' },
  { label: 'duty_cycle', field: 'duty_cycle', unit: 'percent' },
  { label: 'duration', field: 'duration_ms', unit: 's', decimals: 3 }
]

const useModeValues = () => useFormikContext<FeederDosingModeValues>().values

const columnRanges = (
  high: number | null,
  medium: number | null
): Record<FillLevel, string> | null => {
  if (high === null || medium === null || medium >= high) return null

  return {
    high: modeLabel('column_ranges.high', { min: high }),
    medium: modeLabel('column_ranges.medium', { min: medium, max: high }),
    low: modeLabel('column_ranges.low', { max: medium })
  }
}

interface Column {
  key: string
  // Formik path of the column's record, e.g. "levels.0"
  path: string
  // Spoken name of the column, prefixed to each input's row label
  label: string
  header: ReactNode
}

const rowLabel = (row: ParameterRow) =>
  `${row.group ? `${modeLabel(`parameters.${row.group}`)} ` : ''}${modeLabel(`parameters.${row.label}`)}`

const ParameterTable = ({
  title,
  columns,
  rows
}: {
  title: string
  columns: Column[]
  rows: ParameterRow[]
}) => (
  <table className="dosing-levels">
    <thead>
      <tr>
        <th scope="col" className="dosing-levels__title">
          {title}
        </th>
        {columns.map((column) => (
          <th key={column.key} scope="col" className="dosing-levels__cell">
            {column.header}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, index) => (
        <React.Fragment key={row.field}>
          {row.group && row.group !== rows[index - 1]?.group && (
            <tr
              className={`dosing-levels__group ${index > 0 ? 'dosing-levels__group--divided' : ''}`}
            >
              <th scope="row" className="dosing-levels__group-label">
                {modeLabel(`parameters.${row.group}`)}
              </th>
              {columns.map((column) => (
                <td key={column.key} className="dosing-levels__cell" />
              ))}
            </tr>
          )}
          <tr>
            <th scope="row" className="dosing-levels__param">
              {modeLabel(`parameters.${row.label}`)}
            </th>
            {columns.map((column) => (
              <td key={column.key} className="dosing-levels__cell">
                <NumberField
                  name={`${column.path}.${row.field}`}
                  ariaLabel={`${column.label}: ${rowLabel(row)}`}
                  unit={row.unit}
                  decimals={row.decimals}
                />
              </td>
            ))}
          </tr>
        </React.Fragment>
      ))}
    </tbody>
  </table>
)

export const LevelParametersTable = () => {
  const { levels, high_fill_min_percent, medium_fill_min_percent } =
    useModeValues()
  const ranges = columnRanges(high_fill_min_percent, medium_fill_min_percent)

  const columns = levels.map((level, index) => ({
    key: level.fill_level,
    path: `levels.${index}`,
    label: modeLabel(`levels.${level.fill_level}`),
    header: (
      <>
        <div className="dosing-levels__level">
          <span className="dosing-levels__level-name">
            <FillGlyph level={level.fill_level} />
            {modeLabel(`levels.${level.fill_level}`)}
          </span>
        </div>
        {ranges && (
          <div className="dosing-levels__range">{ranges[level.fill_level]}</div>
        )}
      </>
    )
  }))

  return (
    <ParameterTable
      title={modeLabel('level_parameters')}
      columns={columns}
      rows={LEVEL_ROWS}
    />
  )
}

// Removed saved steps stay in the array (marked _destroy) so server error indexes still line up
export const EmergencyStepsTable = ({ maxSteps }: { maxSteps: number }) => {
  const { emergency_steps: steps } = useModeValues()
  const { setFieldValue } = useFormikContext<FeederDosingModeValues>()
  const readOnly = useContext(FormReadOnlyContext)
  const keptSteps = steps.filter((step) => !step._destroy)

  return (
    <FieldArray name="emergency_steps">
      {({ push, remove }) => {
        const columns = steps.flatMap((step, index) => {
          if (step._destroy) return []

          const removeStep = () =>
            step.id
              ? setFieldValue(`emergency_steps.${index}._destroy`, true)
              : remove(index)
          const label = modeLabel('emergency_step', {
            number: keptSteps.indexOf(step) + 1
          })

          return [
            {
              key: step.id ? `step-${step.id}` : `new-step-${index}`,
              path: `emergency_steps.${index}`,
              label,
              header: (
                <div
                  className="dosing-levels__level"
                  data-testid="emergency-step"
                >
                  <span>{label}</span>
                  {!readOnly && keptSteps.length > 1 && (
                    <button
                      type="button"
                      className="dosing-levels__remove"
                      onClick={removeStep}
                    >
                      {modeLabel('remove_step')}
                    </button>
                  )}
                </div>
              )
            }
          ]
        })

        return (
          <>
            <ParameterTable
              title={modeLabel('emergency_steps')}
              columns={columns}
              rows={STEP_ROWS}
            />
            {!readOnly && keptSteps.length < maxSteps && (
              <button
                type="button"
                className="btn btn-secondary inverted dosing-mode-form__add-step"
                onClick={() => push(emptyEmergencyStep(keptSteps.length + 1))}
              >
                {modeLabel('add_step')}
              </button>
            )}
          </>
        )
      }}
    </FieldArray>
  )
}
